// React Query hooks for the reports endpoints.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export type ReportColumnType = 'string' | 'number' | 'date' | 'currency' | 'percent';

export interface ReportColumn {
  key: string;
  label: string;
  type: ReportColumnType;
}

export type ReportCell = string | number | null;

export interface ReportTable {
  columns: ReportColumn[];
  rows: Record<string, ReportCell>[];
  meta?: Record<string, ReportCell> | null;
}

export type ReportBranch = 'shared' | 'scrap' | 'skip' | 'weighbridge';

export interface ReportInfo {
  key: string;
  title: string;
  branch: ReportBranch;
  description: string | null;
}

interface ListReportsResponse {
  reports: ReportInfo[];
}

interface RunReportResponse {
  key: string;
  result: ReportTable;
}

export function useReportsList() {
  return useQuery<ReportInfo[]>({
    queryKey: ['reports', 'list'],
    queryFn: async () => {
      const res = await apiClient.get<ListReportsResponse>('/reports');
      return res.data.reports;
    },
    staleTime: 5 * 60_000,
  });
}

export function useRunReport(reportKey: string, params: Record<string, string>, enabled: boolean) {
  // Drop undefined/empty values from the search query.
  const cleaned: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== '' && v !== undefined && v !== null) cleaned[k] = v;
  }
  const searchString = new URLSearchParams(cleaned).toString();
  return useQuery<ReportTable>({
    queryKey: ['reports', 'run', reportKey, cleaned] as const,
    queryFn: async () => {
      const res = await apiClient.get<RunReportResponse>(
        `/reports/${encodeURIComponent(reportKey)}${searchString ? `?${searchString}` : ''}`,
      );
      return res.data.result;
    },
    enabled,
    retry: false,
  });
}
